<template>
  <v-container>
    <div class="complaints-edit">
      <v-row class="mt-2 d-flex justify-start">
        <template v-if="published">
          <div class="d-inline">
          <v-chip
            class="ma-2"
            color="orange"
            size="small"
            text-color="white"
          >
            <v-icon
              left
              icon="mdi-eye"
            />
            公開中
          </v-chip>
          <v-btn
            size="x-small"
            variant="outlined"
            rounded="pill"
            @click="cancel"
          >
            非公開に戻す
          </v-btn>
        </div>
        </template>
        <template v-else>
          <div class="d-inline">
            <v-chip
              class="ma-2"
              color="light-green"
              size="small"
              text-color="white"
            >
              <v-icon
                left
                icon="mdi-eye-off-outline"
              />
              非公開
            </v-chip>
            <v-btn
              size="x-small"
              variant="outlined"
              rounded="pill"
              @click="fixed"
            >
              公開する
            </v-btn>
          </div>
        </template>
      </v-row>

      <v-row class="my-2">
        <v-col>
          <h2 class="text-no-wrap">
            オンライン相談：主訴一覧
          </h2>
        </v-col>
        <v-col class="pl-0 d-flex justify-end align-center">
          <div class="picker-align-right">
            <month-picker
              v-model:selectedDate="selectedDate"
              @on-month-changed="saveSelectedDate"
            />
          </div>
          <v-btn
            v-if="published"
            variant="flat"
            prepend-icon="custom:download"
            height="40"
            border="md"
            @click="downloadCSV"
            class="text-glance-primary ml-2 font-weight-bold border-current"
          >
            CSVダウンロード
          </v-btn>
        </v-col>
      </v-row>

      <v-table
        v-if="complaints.length > 0"
        class="editable-table table-grid font-size-10-rem"
      >
        <thead class="height-40">
          <tr>
            <th
              class="w-15 text-center font-weight-bold"
            >
              相談相手
            </th>
            <th
              class="w-20 text-center font-weight-bold"
            >
              サービス種別
            </th>
            <th
              class="text-center font-weight-bold"
            >
              主訴
            </th>
            <th
              class="w-25 text-center font-weight-bold"
            >
              子の年齢（小児科のみ）
            </th>
          </tr>
        </thead>
        <tbody class="height-45">
          <tr
            v-for="complaint in complaints"
            :key="complaint.id"
            :class="[isSyounika(complaint.departmentType) ? 'bg-syounika' : 'bg-obstetrics']"
          >
            <td class="text-center">
              {{ complaint.departmentTypeI18n }}
            </td>
            <td class="text-center">
              {{ complaint.reservationTypeI18n }}
            </td>
            <td>
              <template v-if="editable">
                <v-textarea
                  v-model="complaint.complaint "
                  class="my-1"
                  rows="2"
                  clearable
                  dense
                  hide-details
                />
              </template>
              <template v-else>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="newLineCodeToBr(complaint.complaint)" />
              </template>
            </td>
            <td class="text-center">
              <template v-if="editable">
                <v-text-field
                  v-model="complaint.childAge"
                  class="my-1"
                  rows="2"
                  clearable
                  dense
                  hide-details
                />
              </template>
              <template v-else>
                {{ complaint.childAge }}
              </template>
            </td>
          </tr>
        </tbody>
      </v-table>

      <v-row class="my-3 d-flex justify-center">
        <v-btn
          v-if="!published && !editable"
          class="px-8 text-white font-weight-bold"
          :color="color.mainColor()"
          @click="edit"
        >
          編集
        </v-btn>
        <v-btn
          v-if="!published && editable"
          class="mr-2 px-8"
          :color="color.mainColor()"
          variant="outlined"
          @click="reset"
        >
          キャンセル
        </v-btn>
        <v-btn
          v-if="!published && editable"
          class="ml-2 px-8 text-white font-weight-bold"
          :color="color.mainColor()"
          @click="save"
        >
          保存
        </v-btn>
      </v-row>
      <confirm-dialog
        :show-dialog="showDialog"
        :dialog-title="dialogTitle"
        :dialog-message="dialogMessage"
        @on-click-dialog-cancel="closeDialog"
        @on-click-dialog-ok="published ? commitCancel() : commitFixed()"
      />
    </div>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref } from "vue"
import { useStore } from "../stores/index"
import { Complaint } from "../stores/store_types"
import { dateFormatter } from "../service/date_formatter"
import { textFormatter } from "../service/text_formatter"
import { warningMessage } from "../service/warning_message"
import { dateUtility } from "../service/date_utility"
import monthPicker from "./shared/MonthPicker.vue"
import confirmDialog from "./shared/ConfirmDialog.vue"
import { themeColor } from "../service/theme_color"

export default defineComponent({
  name: "ComplaintsEditPage",
  components: {
    monthPicker,
    confirmDialog
  },
  setup() {
    // vuex
    const store = useStore()
    // settings
    const color = themeColor()

    // data
    const corporation = computed(() => store.getters.corporation)
    const complaints = computed(() => store.getters.complaints)
    const published = computed(() => store.getters.complaintsPublished)
    const complaintPublicAvailability = computed(() => store.getters.complaintPublicAvailability)
    const selectedDate = computed(() => new Date(store.getters.selectedComplaintsDate))
    const editable = ref<boolean>(false)
    const dateFormat = dateFormatter()
    const newLineCodeToBr = textFormatter().newLineCodeToBr
    const showDialog = ref<boolean>(false)
    const dialogTitle = ref<string>("")
    const dialogMessage = ref<string>("")

    const isSyounika = (departmentType: string) => {
      if (departmentType == "pediatrician") {
        return true
      }

      return false
    }

    const saveSelectedDate = async (selectedValue: Date) => {
      // 編集モードを解除する
      editable.value = false
      await store.dispatch("commitSelectedComplaintsDate", selectedValue)
    }

    const fixed = async () => {
      dialogTitle.value = "主訴一覧の公開"
      let message = `【${corporation.value.name}】様の【${dateFormat.formatYearMonthJp(selectedDate.value)}】主訴一覧を公開します。よろしいですか？`
      if( dateUtility().isCurrentMonthSelected(selectedDate.value) ){
        message = message + warningMessage
      }
      dialogMessage.value = message
      openDialog()
    }

    const commitFixed = async () => {
      await store.dispatch("createComplaintPublicAvailability")
      closeDialog()
    }

    const cancel = async () => {
      dialogTitle.value = "主訴一覧の非公開"
      let message = `【${corporation.value.name}】様の【${dateFormat.formatYearMonthJp(selectedDate.value)}】主訴一覧を非公開にします。よろしいですか？`
      dialogMessage.value = message
      openDialog()
    }

    const commitCancel = async () => {
      await store.dispatch("deleteComplaintPublicAvailability", complaintPublicAvailability.value.id)
      closeDialog()
    }

    const edit = async () => {
      // 再読み込み
      await store.dispatch("fetchComplaints").then(() => {
        editable.value = true
      })
    }

    const save = async () => {
      const params: {id: number, complaint: string, childAge: string}[] = []
      complaints.value.forEach((complaint: Complaint) => {
        params.push({
          id: complaint.id,
          complaint: complaint.complaint,
          childAge: complaint.childAge,
        })
      })
      await store.dispatch("updateComplaints", params).then(() => {
        editable.value = false
      })
    }

    const reset = async () => {
      // 再読み込み
      await store.dispatch("fetchComplaints").then(() => {
        editable.value = false
      })
    }

    const downloadCSV = async () => {
      await store.dispatch("downloadComplaints")
    }

    const openDialog = () => {
      showDialog.value = true
    }

    const closeDialog = () => {
      showDialog.value = false
    }

    onMounted(() => {
      store.dispatch("commitSelectedComplaintsDate", selectedDate.value)
    })

    return {
      selectedDate,
      complaints,
      published,
      editable,
      saveSelectedDate,
      fixed,
      cancel,
      edit,
      save,
      reset,
      downloadCSV,
      newLineCodeToBr,
      isSyounika,
      showDialog,
      dialogTitle,
      dialogMessage,
      commitFixed,
      commitCancel,
      closeDialog,
      color
    }
  }
})
</script>
